/* RESPONSIBLE TEAM: team-knowledge-foundations */

import {
  BaseConfig,
  EMOJI_TYPEAHEAD,
  INPUT_RULE_CODE_BLOCK,
  INPUT_RULE_EMOJI,
  INPUT_RULE_ORDERED_NESTED_LIST,
  INPUT_RULE_UNORDERED_NESTED_LIST,
  INPUT_RULE_INLINE_CODE,
} from '@intercom/composer-ember';
import {
  createDynamicContentReferenceHandler,
  createDynamicContentTemplateHandler,
} from './dynamic-content-handlers';
import EmbercomFileUploader from './embercom-file-uploader';
import type ComposerConfig from '@intercom/composer-ember/lib/config/composer-config';
import {
  type TextDirection,
  type Analytics,
} from '@intercom/composer-ember/lib/config/composer-config';
import { type Heading, type Block } from '@intercom/interblocks.ts';
import { type UploadConfig } from '@intercom/composer-ember/addon/lib/config/composer-config';
import { EntityType } from 'embercom/models/data/entity-types';

export interface DynamicContentItemVariation {
  locale: string;
  textContent: string;
  jsonBlocks: Block[];
}

export interface DynamicContentItem {
  key: string;
  name: string;
  contentType: string;
  defaultLocale: string;
  variations: DynamicContentItemVariation[];
}

export default class ArticlesComposerConfig extends BaseConfig {
  customBlockHandlers?: ComposerConfig['customBlockHandlers'];
  customInlineHandlers?: ComposerConfig['customInlineHandlers'];
  upload = {
    allowedImageFileTypes: ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'],
    uploader: EmbercomFileUploader,
  } as UploadConfig;
  typeaheads = [EMOJI_TYPEAHEAD];
  placeholder = 'Start writing…';
  showAllTextAlignment = true;
  analytics: Analytics;
  formatters?: ComposerConfig['formatters'];
  allowImplicitMarginParagraphs = false;

  constructor(
    app: any,
    textDirection: TextDirection = 'ltr',
    analytics: Analytics,
    anchorLinkOnClick: (heading: Heading) => void,
    useAnchorLinkArticleInserter = false,
    showMediaToolbar = true,
    dynamicContentItems: DynamicContentItem[] = [],
    articleLocale = '',
  ) {
    super();
    this.upload.attrs = { policyUrl: `/apps/${app.id}/uploads` };
    this.upload.allowedAttachmentTypes = ['*'];

    this.experimental.inlineResizeImages = true;

    if (app.canUseDisableNoMarginClassTransformation) {
      this.allowImplicitMarginParagraphs = true;
    }

    this.textDirection = textDirection;

    let tools: any[] = [
      { name: 'text-formatter' },
      {
        name: useAnchorLinkArticleInserter ? 'content-anchor-editor' : 'anchor-editor',
        options: useAnchorLinkArticleInserter
          ? {
              useTeammateUrls: useAnchorLinkArticleInserter,
              entityTypes: [EntityType.ArticleContent],
            }
          : {},
      },
      { name: 'video-editor' },
      { name: 'button-editor' },
      { name: 'image-editor', options: { supportAltAttributeEditing: true } },
      { name: 'anchor-link-copy-button', options: { onClick: anchorLinkOnClick } },
      { name: 'table-editor' },
      { name: 'color-popover-editor' },
      { name: 'collapsible-toggle' },
    ];

    if (showMediaToolbar) {
      tools.push({
        name: 'media-toolbar',
        options: { showListToolbarIcon: true, dynamicContentItems },
      });
    }

    if (dynamicContentItems.length > 0) {
      this.customBlockHandlers = [
        createDynamicContentReferenceHandler(dynamicContentItems, articleLocale),
      ];
      this.customInlineHandlers = [
        createDynamicContentTemplateHandler(dynamicContentItems, articleLocale),
      ];
    }

    let blocks = [
      'paragraph',
      'image',
      'inlineImage',
      'orderedNestedList',
      'unorderedNestedList',
      'heading',
      'subheading',
      'subheading3',
      'subheading4',
      'codeBlock',
      'video',
      'button',
      'horizontalRule',
      'table',
      'callout',
      'collapsibleSection',
    ];

    this.allowedInline.push('inlineImage');

    this.tables = {
      resizeColumns: true,
      responsive: false,
      containers: false,
    };

    this.inputRules = [
      INPUT_RULE_CODE_BLOCK,
      INPUT_RULE_EMOJI,
      INPUT_RULE_ORDERED_NESTED_LIST,
      INPUT_RULE_UNORDERED_NESTED_LIST,
      INPUT_RULE_INLINE_CODE,
    ];

    this.tools = tools;
    this.allowedBlocks = blocks;
    this.analytics = analytics;
    this.isInteractivityDisabled = false;
  }
}
